import { useMemo } from "react";
import type { Document as AppDocument } from "~/interface/document";
import type {
  AppDesignFlatContent,
  AppDesignNestedContent,
  AppDesignParsedContent,
  CustomizeSpecParsedContent,
  FieldDesignParsedContent,
  TableData,
} from "../types";

type ContentType = "markdown" | "json" | "unknown-json";

interface ParseResult<T> {
  type: ContentType;
  content: string;
  parsed: T | null;
  isMarkdown: boolean;
  /** JSONとしてパースできたが、期待する形式ではない場合true */
  isUnknownJson: boolean;
}

/**
 * formDataまたはcontentから生のコンテンツを取得
 */
const getRawContent = (formData?: AppDocument[], content?: string): string => {
  if (content) return content;
  if (formData?.[0]?.content) {
    return typeof formData[0].content === "string"
      ? formData[0].content
      : JSON.stringify(formData[0].content);
  }
  return "";
};

/**
 * コンテンツを正規化してパース
 */
const parseContent = <T>(
  rawContent: string,
  validator: (parsed: unknown) => boolean,
): ParseResult<T> => {
  if (!rawContent) {
    return { type: "markdown", content: "", parsed: null, isMarkdown: true, isUnknownJson: false };
  }

  try {
    const parsed = JSON.parse(rawContent);
    if (validator(parsed)) {
      return {
        type: "json",
        content: rawContent,
        parsed: parsed as T,
        isMarkdown: false,
        isUnknownJson: false,
      };
    }
    // JSONとしてパースできたが、期待する形式ではない
    return {
      type: "unknown-json",
      content: rawContent,
      parsed: null,
      isMarkdown: false,
      isUnknownJson: true,
    };
  } catch {
    return {
      type: "markdown",
      content: rawContent,
      parsed: null,
      isMarkdown: true,
      isUnknownJson: false,
    };
  }
};

/**
 * 空のTableDataを作成
 */
const emptyTableData = (): TableData => ({ headers: [], rows: [] });

/**
 * フラット構造（英語キー）かどうかを判定
 */
const isFlatStructure = (parsed: unknown): parsed is AppDesignFlatContent => {
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return false;
  const p = parsed as Record<string, unknown>;
  const appTable = p["app_table"] as { rows?: unknown } | undefined;
  const connectorTable = p["connector_table"] as { rows?: unknown } | undefined;
  const functionTable = p["function_table"] as { rows?: unknown } | undefined;
  return (
    Array.isArray(appTable?.rows) &&
    Array.isArray(connectorTable?.rows) &&
    Array.isArray(functionTable?.rows)
  );
};

/**
 * ネスト構造（日本語キー）かどうかを判定
 * - "テーブル一覧" キー: フロントエンドで保存した形式
 * - "tables" キー: バックエンドのcomputed_fieldで追加される形式
 */
const isNestedStructure = (parsed: unknown): parsed is AppDesignNestedContent => {
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return false;
  const p = parsed as Record<string, unknown>;
  // テーブル一覧 または tables キーをチェック
  const tableContainer = p["テーブル一覧"] ?? p["tables"];
  return typeof tableContainer === "object" && tableContainer !== null;
};

/**
 * ネスト構造のテーブルコンテナを取得
 */
const getNestedTableContainer = (
  parsed: AppDesignNestedContent,
): AppDesignNestedContent["テーブル一覧"] => {
  const p = parsed as unknown as Record<string, unknown>;
  return (p["テーブル一覧"] ?? p["tables"]) as AppDesignNestedContent["テーブル一覧"];
};

/**
 * アプリ設計書のデータを正規化（両形式を統一形式に変換）
 */
const normalizeAppDesignData = (parsed: unknown): AppDesignParsedContent | null => {
  if (isFlatStructure(parsed)) {
    return {
      appTable: parsed.app_table || emptyTableData(),
      connectorTable: parsed.connector_table || emptyTableData(),
      functionTable: parsed.function_table || emptyTableData(),
      considerations: parsed.considerations || [],
    };
  }

  if (isNestedStructure(parsed)) {
    const tables = getNestedTableContainer(parsed);
    const p = parsed as unknown as Record<string, unknown>;
    return {
      appTable: tables["アプリテーブル"] || emptyTableData(),
      connectorTable: tables["アプリ間連携テーブル"] || emptyTableData(),
      functionTable: tables["要求機能テーブル"] || emptyTableData(),
      considerations: (p["考慮点"] ?? p["considerations"] ?? []) as string[],
    };
  }

  return null;
};

/**
 * アプリ設計書のデータ変換hook
 */
export const useAppDesignData = (formData?: AppDocument[], content?: string) => {
  return useMemo(() => {
    const rawContent = getRawContent(formData, content);
    if (!rawContent) {
      return {
        type: "markdown" as ContentType,
        content: "",
        parsed: null,
        isMarkdown: true,
        isUnknownJson: false,
      };
    }

    try {
      const parsed = JSON.parse(rawContent);
      const normalized = normalizeAppDesignData(parsed);
      if (normalized) {
        return {
          type: "json" as ContentType,
          content: rawContent,
          parsed: normalized,
          isMarkdown: false,
          isUnknownJson: false,
        };
      }
      // JSONとしてパースできたが、期待する形式ではない
      return {
        type: "unknown-json" as ContentType,
        content: rawContent,
        parsed: null,
        isMarkdown: false,
        isUnknownJson: true,
      };
    } catch {
      return {
        type: "markdown" as ContentType,
        content: rawContent,
        parsed: null,
        isMarkdown: true,
        isUnknownJson: false,
      };
    }
  }, [formData, content]);
};

/**
 * フィールド設計書のデータ変換hook
 */
export const useFieldDesignData = (formData?: AppDocument[], content?: string) => {
  return useMemo(() => {
    const rawContent = getRawContent(formData, content);
    return parseContent<FieldDesignParsedContent>(rawContent, (parsed) => {
      if (typeof parsed !== "object" || parsed === null) return false;
      return Array.isArray((parsed as Record<string, unknown>)["apps"]);
    });
  }, [formData, content]);
};

/**
 * カスタマイズ仕様書のデータ変換hook
 */
export const useCustomizeSpecData = (formData?: AppDocument[], content?: string) => {
  return useMemo(() => {
    const rawContent = getRawContent(formData, content);
    return parseContent<CustomizeSpecParsedContent>(rawContent, (parsed) => {
      if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return false;
      const p = parsed as Record<string, unknown>;
      return Array.isArray(p["headers"]) && Array.isArray(p["rows"]);
    });
  }, [formData, content]);
};
